import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField,
  Button, IconButton, useTheme, useMediaQuery, Chip
} from '@mui/material';
import { Close as CloseIcon, Phone as PhoneIcon, Person as PersonIcon, Verified, FlashOn, Shield } from '@mui/icons-material';
import Cookies from 'js-cookie';
import { invokeApi, apiList } from '../../services/apiServices';
import { config } from '../../config/config';
import popupImg from '../../assets/ar-popup-services.png';

const LeadPopup = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !phone) return;
    setLoading(true);
    try {
      const response = await invokeApi(config.apiUrl + apiList.addLead, {
        name,
        phone,
        personType: 'Website Visitor'
      });
      if (response?.status === 200) {
        Cookies.set('amrutha_lead_interacted', 'true', { expires: 365 });
        setOpen(false);
        alert('Thank you! Our expert will contact you shortly.');
      }
    } catch (error) {
      console.error('Lead submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'transparent',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          m: { xs: 1, sm: 2 },
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minHeight: { xs: 'auto', sm: 500 } }}>

          {/* Left Column — Full Image (55% width) */}
          <Box
            sx={{
              width: { xs: '100%', sm: '55%' },
              minHeight: { xs: 220, sm: 'auto' },
              position: 'relative',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {/* Blurred background image */}
            <Box
              component="img"
              src={popupImg}
              alt="AR Infotech Services"
              sx={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
            {/* Dark overlay gradient */}
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                background: 'linear-gradient(135deg, rgba(10,22,60,0.55) 0%, rgba(14,58,122,0.4) 100%)',
              }}
            />
            {/* Overlay text */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: { xs: 3, sm: 4 },
              }}
            >

            </Box>
          </Box>

          {/* Right Column — Form */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: '#FFFFFF',
              position: 'relative',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 10,
                top: 10,
                zIndex: 10,
                backgroundColor: 'rgba(0,0,0,0.06)',
                color: '#555',
                '&:hover': { backgroundColor: '#fee', color: '#E53935' },
                width: 32,
                height: 32,
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>

            <Chip
              label="🎯 Special Offer"
              size="small"
              sx={{
                mb: 2, alignSelf: 'flex-start',
                backgroundColor: 'rgba(14,106,218,0.1)',
                color: '#0e6ada',
                fontWeight: 700,
                fontSize: '0.75rem'
              }}
            />

            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: '#0e3a7a', lineHeight: 1.3 }}>
              Get Genuine Microsoft Licenses
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Leave your details and get expert help choosing the right Windows, Office, or Server license at the best price.
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Your Name"
                variant="outlined"
                margin="dense"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                size="small"
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: '#0e6ada', mr: 1, fontSize: '1.2rem' }} />
                }}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                margin="dense"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                size="small"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ color: '#0e6ada', mr: 1, fontSize: '1.2rem' }} />
                }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
                  boxShadow: '0 6px 20px rgba(14,106,218,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0b54b0 0%, #0080aa 100%)',
                    boxShadow: '0 8px 24px rgba(14,106,218,0.45)',
                  }
                }}
              >
                {loading ? 'Submitting...' : 'Get Expert Advice →'}
              </Button>
            </form>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center', display: 'block' }}>
              🔒 Your information is safe and never shared.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LeadPopup;
